// frontend/src/components/Pricing.js
import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import axios from "../utils/axiosConfig"
import API_URL from "../config"
import { ChevronDown, ChevronRight, Check } from "lucide-react"

// 定價方案元件（PricingPlan）
const PricingPlan = () => {
  const { t } = useTranslation()
  const [planType, setPlanType] = useState("individual")
  const [billingPeriod, setBillingPeriod] = useState("monthly")

  const handleSubscribe = async (plan) => {
    try {
      // 使用現有的 axios 配置進行 API 呼叫範例
      const response = await axios.post(
        `${API_URL}/api/subscribe`,
        { plan, billingPeriod },
        { withCredentials: true }
      )
      toast.success(t("pricingPlan.subscriptionSuccess"))
    } catch (error) {
      toast.error(t("pricingPlan.error.subscriptionFailed"))
      console.error("訂閱錯誤:", error)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      {/* 主橫幅 */}
      <div
        className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white text-3xl font-bold"
        style={{ backgroundImage: "url('/images/myReportsBanner.png')" }}
      >
        {t("pricingPlan.title")}
        <p className="text-lg font-medium mt-2">{t("pricingPlan.subtitle")}</p>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {/* 方案類型選擇器 */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-md shadow-sm bg-white dark:bg-gray-800 p-1">
            <button
              onClick={() => setPlanType("individual")}
              className={`px-8 py-3 text-sm font-medium rounded-md ${
                planType === "individual"
                  ? "bg-gradient-to-r from-blue-400 to-purple-500 text-white"
                  : "text-gray-700 dark:text-gray-300"
              }`}
            >
              {t("pricingPlan.individual")}
            </button>
            <button
              onClick={() => setPlanType("team")}
              className={`px-8 py-3 text-sm font-medium rounded-md ${
                planType === "team"
                  ? "bg-gradient-to-r from-blue-400 to-purple-500 text-white"
                  : "text-gray-700 dark:text-gray-300"
              }`}
            >
              {t("pricingPlan.team")}
            </button>
          </div>
        </div>

        {/* 付費週期選擇器 */}
        <div className="flex justify-center mb-10">
          <div className="grid grid-cols-3 w-full max-w-md">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`py-2 text-center ${
                billingPeriod === "monthly"
                  ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 font-medium"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {t("pricingPlan.monthly")}
            </button>
            <button
              onClick={() => setBillingPeriod("semiAnnually")}
              className={`py-2 text-center ${
                billingPeriod === "semiAnnually"
                  ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 font-medium"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {t("pricingPlan.semiAnnually")}
            </button>
            <button
              onClick={() => setBillingPeriod("annually")}
              className={`py-2 text-center ${
                billingPeriod === "annually"
                  ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400 font-medium"
                  : "text-gray-500 dark:text-gray-400"
              }`}
            >
              {t("pricingPlan.annually")}
            </button>
          </div>
        </div>

        {/* 特別優惠橫幅 */}
        <div className="bg-gradient-to-r from-blue-400 to-purple-500 rounded-lg p-4 mb-10 flex flex-col md:flex-row justify-between items-center text-white">
          <div className="flex-1">
            <p className="text-center md:text-left">
              {t("pricingPlan.exclusiveOffer")}: {t("pricingPlan.experiencePro")}{" "}
              {t("pricingPlan.for3Days")} {t("pricingPlan.at")}{" "}
              <span className="text-2xl font-bold">$9.9</span>
            </p>
          </div>
          <button
            className="mt-4 md:mt-0 px-6 py-2 bg-white text-blue-600 rounded-md font-medium hover:bg-gray-100 transition-colors"
            onClick={() => handleSubscribe("pro-trial")}
          >
            {t("pricingPlan.getNow")}
          </button>
        </div>

        {/* 定價卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* 免費方案 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="p-6 flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("pricingPlan.free")}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t("pricingPlan.forAnyoneInterested")}
              </p>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">$0</span>
                <span className="ml-1 text-xl text-gray-500 dark:text-gray-400">
                  / {t("pricingPlan.month")}
                </span>
              </div>
              <p className="mt-2 text-sm text-blue-500">
                {t("pricingPlan.cryptoAccepted")}
              </p>
              <button
                className="mt-6 w-full bg-white border border-gray-300 dark:border-gray-600 rounded-md py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => handleSubscribe("free")}
              >
                {t("pricingPlan.subscribe")}
              </button>
            </div>
            <div className="px-6 pt-6 pb-8 bg-gray-50 dark:bg-gray-900">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    13 {t("pricingPlan.blockchains")}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.essentialInvestigation")}
                  </p>
                </li>
              </ul>
            </div>
          </div>

          {/* 基礎方案 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="p-6 flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("pricingPlan.basic")}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t("pricingPlan.forEverydayInvestors")}
              </p>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">$30</span>
                <span className="ml-1 text-xl text-gray-500 dark:text-gray-400">
                  / {t("pricingPlan.month")}
                </span>
              </div>
              <p className="mt-2 text-sm text-blue-500">
                {t("pricingPlan.cryptoAccepted")}
              </p>
              <button
                className="mt-6 w-full bg-white border border-gray-300 dark:border-gray-600 rounded-md py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => handleSubscribe("basic")}
              >
                {t("pricingPlan.subscribe")}
              </button>
            </div>
            <div className="px-6 pt-6 pb-8 bg-gray-50 dark:bg-gray-900">
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                {t("pricingPlan.everythingInFree")}:
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.abilityToEditSharedCharts")}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.addMemosToCharts")}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.expandedInterChainTracker")}
                  </p>
                </li>
              </ul>
            </div>
          </div>

          {/* 專業方案 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border-2 border-blue-500 flex flex-col relative">
            <div className="absolute top-0 right-0 bg-red-500 text-white px-4 py-1 text-xs font-bold uppercase tracking-wider">
              {t("pricingPlan.best")}
            </div>
            <div className="p-6 flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("pricingPlan.pro")}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t("pricingPlan.forProfessional")}
              </p>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">$100</span>
                <span className="ml-1 text-xl text-gray-500 dark:text-gray-400">
                  / {t("pricingPlan.month")}
                </span>
              </div>
              <p className="mt-2 text-sm text-blue-500">
                {t("pricingPlan.cryptoAccepted")}
              </p>
              <button
                className="mt-6 w-full bg-gradient-to-r from-blue-400 to-purple-500 rounded-md py-2 text-sm font-medium text-white hover:from-blue-500 hover:to-purple-600 transition-colors"
                onClick={() => handleSubscribe("pro")}
              >
                {t("pricingPlan.subscribe")}
              </button>
            </div>
            <div className="px-6 pt-6 pb-8 bg-gray-50 dark:bg-gray-900">
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                {t("pricingPlan.everythingInBasic")}:
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    ⭐ {t("pricingPlan.solana")}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.addressTxBatch")}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.customWatermark")}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.defaultWatermarkRemoval")}
                  </p>
                </li>
              </ul>
            </div>
          </div>

          {/* 超級方案 */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="p-6 flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t("pricingPlan.ultra")}
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {t("pricingPlan.forLawEnforcement")}
              </p>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">$500</span>
                <span className="ml-1 text-xl text-gray-500 dark:text-gray-400">
                  / {t("pricingPlan.month")}
                </span>
              </div>
              <p className="mt-2 text-sm text-blue-500">
                {t("pricingPlan.cryptoAccepted")}
              </p>
              <button
                className="mt-6 w-full bg-white border border-gray-300 dark:border-gray-600 rounded-md py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                onClick={() => handleSubscribe("ultra")}
              >
                {t("pricingPlan.subscribe")}
              </button>
            </div>
            <div className="px-6 pt-6 pb-8 bg-gray-50 dark:bg-gray-900">
              <h4 className="font-medium text-gray-900 dark:text-white mb-4">
                {t("pricingPlan.everythingInBasic")}:
              </h4>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <p className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    {t("pricingPlan.noLimitation")}
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// 定價比較元件（PricingComparison）

// 直接在元件檔案中整合的 API 服務函數：取得目前訂閱資料
const getCurrentSubscription = async () => {
  try {
    const response = await axios.get(`${API_URL}/api/subscription/current`, {
      withCredentials: true
    })
    return response.data
  } catch (error) {
    console.error("取得目前訂閱資料錯誤:", error)
    throw error
  }
}

// 直接在元件檔案中整合的 API 服務函數：訂閱方案
const subscribeToPlan = async (planId) => {
  try {
    const response = await axios.post(
      `${API_URL}/api/subscription/subscribe`,
      { planId },
      { withCredentials: true }
    )
    return response.data
  } catch (error) {
    console.error("訂閱方案錯誤:", error)
    throw error
  }
}

const PricingComparison = () => {
  const { t } = useTranslation()
  const [isBasicInvestigationOpen, setIsBasicInvestigationOpen] = useState(false)
  const [currentPlan, setCurrentPlan] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // 元件掛載時取得目前訂閱資料
    const fetchCurrentSubscription = async () => {
      try {
        setIsLoading(true)
        const data = await getCurrentSubscription()
        setCurrentPlan(data.planId)
      } catch (error) {
        toast.error(t("pricingPlan.errors.fetchFailed", "取得目前訂閱資料失敗"))
      } finally {
        setIsLoading(false)
      }
    }
    fetchCurrentSubscription()
  }, [t])

  const handleSubscribe = async (planId) => {
    try {
      setIsLoading(true)
      await subscribeToPlan(planId)
      setCurrentPlan(planId)
      toast.success(t("pricingPlan.subscriptionSuccess", "訂閱方案成功"))
    } catch (error) {
      toast.error(t("pricingPlan.errors.subscribeFailed", "訂閱方案失敗"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      {/* 定價表容器 */}
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="overflow-x-auto">
          <div className="min-w-max">
            {/* 定價表 */}
            <div className="grid grid-cols-5 gap-0 border-b">
              {/* 空白格，用於放置功能名稱 */}
              <div className="p-4"></div>

              {/* 免費方案 */}
              <div className="p-4 text-center">
                <h3 className="text-xl font-semibold">
                  {t("pricingPlan.free", "Free")}
                </h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">
                    ${t("pricingPlan.freePrice", "0")}
                  </span>
                  <span className="text-sm text-gray-500">
                    /{t("pricingPlan.month", "month")}
                  </span>
                </div>
                <div className="mt-4">
                  <button
                    className={`w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium ${
                      currentPlan === "free"
                        ? "bg-gray-100 text-gray-700"
                        : "bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                    disabled={currentPlan === "free" || isLoading}
                    onClick={() => currentPlan !== "free" && handleSubscribe("free")}
                  >
                    {currentPlan === "free"
                      ? t("pricingPlan.currentPlan", "Your current plan")
                      : t("pricingPlan.subscribe", "Subscribe")}
                  </button>
                </div>
              </div>

              {/* 基礎方案 */}
              <div className="p-4 text-center">
                <h3 className="text-xl font-semibold">
                  {t("pricingPlan.basic", "Basic")}
                </h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">
                    ${t("pricingPlan.basicPrice", "30")}
                  </span>
                  <span className="text-sm text-gray-500">
                    /{t("pricingPlan.month", "month")}
                  </span>
                </div>
                <div className="mt-4">
                  <button
                    className={`w-full py-2 px-4 border border-blue-500 rounded-md text-sm font-medium ${
                      currentPlan === "basic"
                        ? "bg-blue-50 text-blue-600"
                        : "bg-white text-blue-500 hover:bg-blue-50"
                    }`}
                    disabled={currentPlan === "basic" || isLoading}
                    onClick={() => currentPlan !== "basic" && handleSubscribe("basic")}
                  >
                    {currentPlan === "basic"
                      ? t("pricingPlan.currentPlan", "Your current plan")
                      : t("pricingPlan.subscribe", "Subscribe")}
                  </button>
                </div>
              </div>

              {/* 專業方案 */}
              <div className="p-4 text-center relative border-l border-r border-t rounded-t-lg">
                <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-1 rounded-md font-bold text-sm">
                  {t("pricingPlan.best", "BEST")}
                </div>
                <h3 className="text-xl font-semibold text-blue-600">
                  {t("pricingPlan.pro", "Pro")}
                </h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold text-blue-600">
                    ${t("pricingPlan.proPrice", "100")}
                  </span>
                  <span className="text-sm text-gray-500">
                    /{t("pricingPlan.month", "month")}
                  </span>
                </div>
                <div className="mt-4">
                  <button
                    className={`w-full py-2 px-4 rounded-md text-sm font-medium ${
                      currentPlan === "pro"
                        ? "bg-blue-700 text-white"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                    disabled={currentPlan === "pro" || isLoading}
                    onClick={() => currentPlan !== "pro" && handleSubscribe("pro")}
                  >
                    {currentPlan === "pro"
                      ? t("pricingPlan.currentPlan", "Your current plan")
                      : t("pricingPlan.subscribe", "Subscribe")}
                  </button>
                </div>
              </div>

              {/* 超級方案 */}
              <div className="p-4 text-center">
                <h3 className="text-xl font-semibold">
                  {t("pricingPlan.ultra", "Ultra")}
                </h3>
                <div className="mt-2">
                  <span className="text-3xl font-bold">
                    ${t("pricingPlan.ultraPrice", "500")}
                  </span>
                  <span className="text-sm text-gray-500">
                    /{t("pricingPlan.month", "month")}
                  </span>
                </div>
                <div className="mt-4">
                  <button
                    className={`w-full py-2 px-4 border border-blue-500 rounded-md text-sm font-medium ${
                      currentPlan === "ultra"
                        ? "bg-blue-50 text-blue-600"
                        : "bg-white text-blue-500 hover:bg-blue-50"
                    }`}
                    disabled={currentPlan === "ultra" || isLoading}
                    onClick={() => currentPlan !== "ultra" && handleSubscribe("ultra")}
                  >
                    {currentPlan === "ultra"
                      ? t("pricingPlan.currentPlan", "Your current plan")
                      : t("pricingPlan.subscribe", "Subscribe")}
                  </button>
                </div>
              </div>
            </div>

            {/* 支援的區塊鏈區段 */}
            <div className="border-b">
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="text-sm text-gray-500">
                    {t("pricingPlan.supportedBlockchains", "Supported Blockchains")}
                  </p>
                  <p className="font-medium">
                    {t("pricingPlan.chains", "14 chains")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* Solana 列 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4 flex items-center">
                  <div className="h-4 w-4 rounded-full bg-purple-700 mr-2"></div>
                  <p className="font-medium">{t("pricingPlan.solana", "Solana")}</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>
            </div>

            {/* 調查區段 */}
            <div className="border-b">
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="text-sm text-gray-500">
                    {t("pricingPlan.investigation", "Investigation")}
                  </p>
                  <div
                    className="font-medium flex items-center cursor-pointer"
                    onClick={() =>
                      setIsBasicInvestigationOpen(!isBasicInvestigationOpen)
                    }
                  >
                    {t("pricingPlan.basicInvestigation", "Basic Investigation")}
                    {isBasicInvestigationOpen ? (
                      <ChevronDown className="h-4 w-4 ml-1" />
                    ) : (
                      <ChevronRight className="h-4 w-4 ml-1" />
                    )}
                  </div>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    200 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    2,000 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    5,000 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 基礎調查詳情（可折疊） */}
              {isBasicInvestigationOpen && (
                <div className="grid grid-cols-5 gap-0 bg-gray-50 dark:bg-gray-800">
                  <div className="p-4 col-span-5">
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {t(
                        "pricingPlan.basicInvestigationDesc",
                        "Basic Investigation includes the general investigation, Entity Identification, Expand Incoming/Outgoing, Load More, Add Single Address/Tx."
                      )}
                    </p>
                  </div>
                </div>
              )}

              {/* 交易對手與金額範圍 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4 col-span-5 bg-gray-50 dark:bg-gray-800">
                  <p className="text-sm text-center">
                    {t(
                      "pricingPlan.counterpartyUnavailable",
                      "Counterparty and Amount Range unavailable"
                    )}
                  </p>
                </div>
              </div>

              {/* 進階分析 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">
                    {t("pricingPlan.advancedAnalysis", "Advanced Analysis")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    5 / {t("pricingPlan.month", "Month")}{" "}
                    <span className="text-xs text-blue-500">
                      {t("pricingPlan.restrictions", "Restrictions")}
                    </span>
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    50 / {t("pricingPlan.month", "Month")}{" "}
                    <span className="text-xs text-blue-500">
                      {t("pricingPlan.restrictions", "Restrictions")}
                    </span>
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    250 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 跨鏈追蹤器 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4 flex items-center">
                  <p className="font-medium">
                    {t("pricingPlan.interChainTracker", "InterChain Tracker")}
                  </p>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    10 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    200 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    2,000 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 地址/交易批次導入 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">
                    {t("pricingPlan.addressBatch", "Address/Tx Batch Importing")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    100 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 備忘錄 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">{t("pricingPlan.memo", "Memo")}</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 自訂浮水印 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">
                    {t("pricingPlan.customWatermark", "Custom Watermark")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* MetaSleuth 浮水印移除 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">
                    {t("pricingPlan.metaSleuthWatermark", "MetaSleuth Watermark Removal")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>
            </div>

            {/* 協作與資料區段 */}
            <div className="border-b">
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="text-sm text-gray-500">
                    {t("pricingPlan.collaborationAndData", "Collaboration and Data")}
                  </p>
                  <p className="font-medium">{t("pricingPlan.label", "Label")}</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">10</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">100</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">2,000</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 已儲存圖表 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">{t("pricingPlan.savedChart", "Saved Chart")}</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">5</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">50</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">500</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 共用連結 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">{t("pricingPlan.sharedLink", "Shared Link")}</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">5</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">50</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">1,000</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 將分析資料匯出為 CSV */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">
                    {t("pricingPlan.exportingAnalysis", "Exporting Analysis Data as CSV")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    5 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    50 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    250 / {t("pricingPlan.month", "Month")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 共用連結編輯 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4 flex items-center">
                  <p className="font-medium">
                    {t("pricingPlan.sharedLinkEdit", "Shared Link Edit")}
                  </p>
                  <ChevronRight className="h-4 w-4 ml-1" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <span className="text-gray-400">—</span>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>
            </div>

            {/* 監控區段 */}
            <div className="border-b">
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="text-sm text-gray-500">
                    {t("pricingPlan.monitor", "Monitor")}
                  </p>
                  <p className="font-medium">{t("pricingPlan.address", "Address")}</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">1</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">50</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">500</p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>

              {/* 通知 */}
              <div className="grid grid-cols-5 gap-0">
                <div className="p-4">
                  <p className="font-medium">
                    {t("pricingPlan.notification", "Notification")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    10 / {t("pricingPlan.day", "Day")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    100 / {t("pricingPlan.day", "Day")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <p className="text-sm">
                    1,000 / {t("pricingPlan.day", "Day")}
                  </p>
                </div>
                <div className="p-4 flex justify-center items-center">
                  <Check className="text-green-500 h-5 w-5" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// 主頁元件：包含定價方案與定價比較
const PricingPage = () => {
  return (
    <div>
      <PricingPlan />
      <PricingComparison />
    </div>
  )
}

export default PricingPage
